
  import { defineComponent, onMounted, ref } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter, useRoute } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { ExpertReviewRejectReasonObject } from '@/store/modules/Indices/ExpertReviewRejectReasonsModule';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';

  export default defineComponent({
    name: 'expert-review-reject-reasons-editing',
    components: {
      ErrorMessage,
      Field,
      Form,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const route = useRoute();
      const { can } = useAbility();
      const submitButton = ref<HTMLButtonElement | null>(null);

      const translate = (text: string) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(
          translate('expertReviewRejectReasonUpdating'),
          [translate('indices')]
        );
      });

      let itemRes;
      try {
        itemRes = await store.dispatch(
          Actions.GET_EXPERT_REVIEW_REJECT_REASON,
          route.params.id
        );
      } catch {
        store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');
        Swal.fire({
          text: translate('expertReviewRejectReasonLoadFailed'),
          icon: 'error',
          buttonsStyling: false,
          confirmButtonText: translate('ok'),
          customClass: { confirmButton: 'btn fw-bold btn-light-danger' },
        }).then(() =>
          router.push({ name: 'expert-review-reject-reasons-listing' })
        );
      }

      const loaded = itemRes?.data?.data ?? {};

      // The reject-reasons API reads back snake_case (name_en, is_active) but
      // accepts camelCase on write, so the loaded row is mapped onto the shape
      // the form and the update DTO both use.
      const item = ref<ExpertReviewRejectReasonObject>({
        ...loaded,
        nameEn: loaded.nameEn ?? loaded.name_en,
        nameAr: loaded.nameAr ?? loaded.name_ar,
        isActive: loaded.isActive ?? loaded.is_active,
      } as ExpertReviewRejectReasonObject);

      const goBack = () => {
        router.back();
      };

      const validationSchema = Yup.object().shape({
        nameAr: Yup.string()
          .required(() => translate('REASON_AR_IS_REQUIRED_FIELD'))
          .label('Reason Ar'),
        nameEn: Yup.string()
          .required(() => translate('REASON_EN_IS_REQUIRED_FIELD'))
          .label('Reason En'),
      });

      const onSubmitUpdate = async (values) => {
        if (submitButton.value) {
          submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        const payload = {
          nameAr: values.nameAr,
          nameEn: values.nameEn,
        };

        await store.dispatch(Actions.UPDATE_EXPERT_REVIEW_REJECT_REASON, {
          id: item.value?.id,
          data: payload,
        });
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          submitButton.value?.removeAttribute('data-kt-indicator');
          submitButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_UPDATED_EXPERT_REVIEW_REJECT_REASON'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            router.push({ name: 'expert-review-reject-reasons-listing' });
          });
        }
      };

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        onSubmitUpdate,
        submitButton,
        translate,
        validationSchema,
        goBack,
        item,
        can,
      };
    },
  });
